import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, HelpCircle, ArrowRight, CheckCircle } from 'lucide-react';
import { topicsApi, Topic, Theme } from '../api/topics';
import { useModuleCompletion } from '../hooks/useModuleCompletion';

// Image mapping for topics based on their slug or content - each topic gets a unique image
const getImageForTopic = (topic: Topic): string => {
  const slug = topic.slug?.toLowerCase() || '';
  const title = topic.title?.toLowerCase() || '';
  
  // AI Fundamentals - Introduction/learning image
  if (slug.includes('ai-fundamentals') || title.includes('fundamentals')) {
    return 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop&crop=center';
  } 
  // Data & AI - Data visualization image
  else if (slug.includes('data') || title.includes('data')) {
    return 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&crop=center';
  } 
  // GPT models - Neural network/AI image
  else if (slug.includes('gpt') || title.includes('gpt') || title.includes('revolutie')) {
    return 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop&crop=center';
  } 
  // Competition & AI models - Business/competition image
  else if (slug.includes('concurrentie') || title.includes('concurrentie')) {
    return 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=250&fit=crop&crop=center';
  } 
  // Prompt Engineering - Coding/programming image
  else if (slug.includes('prompt') || title.includes('prompt') || title.includes('engineering')) {
    return 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&crop=center';
  } 
  // Applications & Future - Innovation/future image
  else if (slug.includes('toepassingen') || title.includes('toekomst') || title.includes('applications')) {
    return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center';
  }
  
  // Default AI-related image for any unmatched topics
  return 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop&crop=center';
};

export const Topics: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const { getCompletedCount, getTopicProgress } = useModuleCompletion();
  
  const selectedTheme = searchParams.get('theme');

  useEffect(() => {
    // Scroll to top when component mounts or theme changes
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch both topics and themes
        const [topicsData, themesData] = await Promise.all([
          topicsApi.getAll(),
          topicsApi.getThemes()
        ]);
        
        setTopics(Array.isArray(topicsData) ? topicsData : []);
        setThemes(Array.isArray(themesData) ? themesData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setTopics([]);
        setThemes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedTheme]);

  // Filter topics by theme if theme parameter is provided
  const filteredTopics = selectedTheme 
    ? topics.filter(topic => topic.theme_id && themes.find(theme => theme.id === topic.theme_id && theme.slug === selectedTheme))
    : topics;

  // Get current theme info
  const currentTheme = selectedTheme ? themes.find(theme => theme.slug === selectedTheme) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {currentTheme ? `Thema: ${currentTheme.title}` : 'Alle Trainingen'}
        </h1>
        <p className="text-gray-600">
          {currentTheme 
            ? currentTheme.description || 'Kies een onderwerp om te starten.'
            : 'Ontdek alle beschikbare trainingen. Kies een thema of onderwerp om te starten.'
          }
        </p>
      </div>

      {/* Topics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTopics.length === 0 ? (
          <div className="col-span-full bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-600">
              {selectedTheme ? 'Geen onderwerpen beschikbaar voor dit thema.' : 'Geen onderwerpen beschikbaar.'}
            </p>
          </div>
        ) : (
          filteredTopics.map((topic, index) => {
            const sectionsCount = topic.sections?.length || 0;
            const questionsCount = topic.questions?.length || 0;
            const completedCount = getCompletedCount(topic.id);
            const completionProgress = getTopicProgress(topic.id, sectionsCount);
            const isFullyCompleted = completedCount === sectionsCount && sectionsCount > 0;
            const topicImage = getImageForTopic(topic);
            
            return (
              <div 
                key={topic.id} 
                className={`h-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group flex flex-col ${
                  isFullyCompleted ? 'ring-2 ring-green-200' : ''
                }`}
              >
                <Link to={`/topics/${topic.slug}`} className="flex flex-col h-full">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden flex-shrink-0">
                    <img
                      src={topicImage}
                      alt={topic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {isFullyCompleted && (
                      <div className="absolute top-3 right-3 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>Voltooid</span>
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs font-medium">
                      #{index + 1}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Title */}
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {topic.title.toUpperCase()}
                    </h3>
                    
                    {/* Short Description */}
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed flex-grow line-clamp-3">
                      {topic.summary || 'Leer meer over dit onderwerp met praktische voorbeelden en oefeningen.'}
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center space-x-1">
                          <FileText className="w-3 h-3" />
                          <span>{sectionsCount} modules</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <HelpCircle className="w-3 h-3" />
                          <span>{questionsCount} vragen</span>
                        </span>
                      </div>
                      {completionProgress > 0 && (
                        <span className="font-medium text-blue-600">{completionProgress}%</span>
                      )}
                    </div>
                    
                    {/* Button */}
                    <div className="mt-auto">
                      <button className={`w-full px-4 py-2 rounded-md font-medium text-sm flex items-center justify-center group ${
                        isFullyCompleted
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      } transition-colors`}>
                        <span>{isFullyCompleted ? 'Bekijk opnieuw' : 'Start hier'}</span>
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
